import { useCallback } from 'react'
import TimeAgo from 'react-timeago'
import type { MouseEvent } from 'react'
import { CalendarEvent } from '@highlight/common'
import { Button } from '@highlight/ui'
import { useOpenBrief } from '@renderer/overlay/Sidepanel/components/meeting/brief/use-open-brief'
import { BriefPreview } from '@renderer/overlay/Sidepanel/components/meeting/brief/components/brief-preview'
import { CalendarColorDot } from '@renderer/components/Icons/CalendarEventIcon'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { Toast, useToast } from '../../index'
import { meetingCountdownFormatter } from './utils'
import { useMeetingStartingSoonToast } from './use-meeting-starting-soon-toast'

interface MeetingStartingSoonToastProps {
  meeting: CalendarEvent
  toastId: string
  force?: boolean
}

export function MeetingStartingSoonToast({ meeting, toastId, force = false }: MeetingStartingSoonToastProps) {
  const toast = useToast()
  const openBrief = useOpenBrief()
  const { meetingJoinLink, transcribesOnJoin, joinLabel, transcribeLabel, onJoin, onStartTranscribing } =
    useMeetingStartingSoonToast({ meeting, toastId, force })

  const onReadBrief = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      AmplitudeRenderer.trackEvent('Meeting Brief', {
        action: 'view',
        source: 'starting-soon-toast',
        meeting_id: meeting.id,
      })
      openBrief({
        calendarEventId: meeting.id,
        isActive: false,
        title: meeting.title,
        meeting,
      })
      toast.dismiss(toastId)
    },
    [openBrief, meeting, toast, toastId],
  )

  return (
    <Toast toastId={toastId}>
      <Toast.Content>
        {meeting.brief && (
          <BriefPreview
            brief={meeting.brief.brief}
            relevantLinks={meeting.brief.relevantLinks}
            onClick={onReadBrief}
            disableLinks
          />
        )}
        {transcribesOnJoin && (
          <Toast.Description className="text-foreground-placeholder text-xs">
            Transcription starts automatically when you join.
          </Toast.Description>
        )}
        <Toast.Footer style={{ fontVariantNumeric: 'tabular-nums' }}>
          <span className="flex items-center gap-1.5">
            <CalendarColorDot calendarId={meeting.calendarId} />
            <TimeAgo date={meeting.start} formatter={meetingCountdownFormatter} />
          </span>
          {meetingJoinLink ? (
            <Button
              onClick={(event) => {
                event.stopPropagation()
                onJoin()
              }}
              size="sm"
              variant="primary"
              tone="inverse"
            >
              {joinLabel}
            </Button>
          ) : (
            <Button
              onClick={(event) => {
                event.stopPropagation()
                onStartTranscribing()
              }}
              size="sm"
              variant="primary"
              tone="inverse"
            >
              {transcribeLabel}
            </Button>
          )}
        </Toast.Footer>
      </Toast.Content>
    </Toast>
  )
}
